import React, { useEffect, useState } from "react";
import { Divider, Form, Row, Spin } from "antd";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { LeftOutlined } from "@ant-design/icons";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../..";
import { ITypesCharacteristicsCreateContext } from "../../context/TypesCharacteristicsCreateContext/types";
import { TypesCharacteristicsObjectParams } from "../../features/TypesCharacteristicsObjectParams/TypesCharacteristicsObjectParams";
import { TypesCharacteristicsObjectValues } from "../../features/TypesCharacteristicsObjectValues/TypesCharacteristicsObjectValues";
import "./TypesCharacteristicsObjectView.less";
import { TypesCharacteristicsViewContext } from "../../context/TypesCharacteristicsViewContext/TypesCharacteristicsViewContext";
import {
  deleteTypesCharacteristicsObject,
  fetchTypesCharacteristicsSelectValueDic,
  searchTypesCharacteristicsById,
  searchTypesCharacteristicsObjectById,
  typesCharacteristicsObjectActions,
  updateTypesCharacteristicsObject,
} from "../../store/slice/typesCharacteristicsSlice";
import { TypeCharacteristic } from "../../types/TypeCharacteristic";
import {
  checkIsLoading,
  toHttpFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../../../lib/utils";

export const TypesCharacteristicsObjectView = () => {
  const history = useHistory();
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const { objectCharTypeId, objectTypeId } = useParams<{
    objectTypeId: string;
    objectCharTypeId: string;
  }>();

  const {
    typesCharacteristics,
    typesCharacteristicsObjectById,
    typesCharacteristicsLoading,
    typesCharacteristicsByIdLoading,
    typesCharacteristicsObjectByIdLoading,
    typesCharacteristicsViewModeMaskDicLoading,
    typesCharacteristicsTypesStorageDicLoading,
  } = useAppSelector((state) => state.typesCharacteristicsObject);

  const [typesCharacteristicsObjectParamsForm] = Form.useForm();

  const contextValue: ITypesCharacteristicsCreateContext = {};

  const [selectedCharType, setSelectedCharType] = useState<TypeCharacteristic | undefined>(
    undefined
  );

  const onSelectedCharType = (charTypeId: number) => {
    const findedCharType = typesCharacteristics.find((item) => item.id === charTypeId);

    if (setSelectedCharType) {
      setSelectedCharType(findedCharType);
    }

    if (typesCharacteristicsObjectParamsForm) {
      typesCharacteristicsObjectParamsForm.setFieldsValue({
        defaultValue: undefined,
      });
    }
  };

  const [targetKeys, setTargetKeys] = useState<string[]>([]);

  const isToday = (dateString: string) => {
    const inputDate = moment(dateString).startOf("day");
    const today = moment().startOf("day");
    return inputDate.isSame(today);
  };

  const fetchTypesCharacteristicsObjectById = () => {
    const onDateString = search.replace("?onDate=", "");

    // Проверяем, что дата не является сегодняшней
    const shouldSkipOnDate = onDateString && isToday(onDateString);

    if (objectCharTypeId && objectTypeId) {
      dispatch(
        searchTypesCharacteristicsObjectById({
          objectTypeId,
          objectCharTypeId,
          onDate:
            !shouldSkipOnDate && onDateString ? toHttpFormat(moment(onDateString)) : undefined,
        })
      );
    }
  };

  // Загрузка типа характеристики (объект) по входу
  useEffect(() => {
    fetchTypesCharacteristicsObjectById();
  }, [objectCharTypeId, objectTypeId]);

  // Очистка по выходу
  useEffect(() => {
    return () => {
      dispatch(typesCharacteristicsObjectActions.cleanTypesCharacteristicsObjectById());
    };
  }, [objectCharTypeId, objectTypeId]);

  const setDefaultTargetKeys = () => {
    if (
      typesCharacteristicsObjectById &&
      typesCharacteristicsObjectById?.objectCharTypeValues &&
      Array.isArray(typesCharacteristicsObjectById?.objectCharTypeValues)
    ) {
      setTargetKeys(
        typesCharacteristicsObjectById?.objectCharTypeValues?.map((item) =>
          String(item.charTypeValueId)
        )
      );
    }
  };

  // Загрузка типа характеристики для параметра объекта
  useEffect(() => {
    if (typesCharacteristicsObjectById && typesCharacteristicsObjectById.charTypeId) {
      dispatch(
        searchTypesCharacteristicsById(String(typesCharacteristicsObjectById.charTypeId))
      ).then((res) => {
        if (res.payload) {
          setSelectedCharType(res.payload);

          if (res.payload && res.payload?.valueDicTypeAlias) {
            dispatch(fetchTypesCharacteristicsSelectValueDic(res.payload?.valueDicTypeAlias));
          }

          setDefaultTargetKeys();
        }
      });
    }
  }, [typesCharacteristicsObjectById]);

  const [mode, setMode] = useState<"view" | "edit_close_date" | "edit">("view");

  const onFinish = (fields) => {
    if (mode === "edit_close_date") {
      const params = {
        objectTypeId: Number(objectTypeId),
        objectCharTypeId: Number(objectCharTypeId),
        onDate: toHttpFormatEndOfDay(fields.toDate),
      };

      dispatch(deleteTypesCharacteristicsObject(params)).then((res) => {
        if (res.payload !== undefined) {
          fetchTypesCharacteristicsObjectById();
          setMode("view");
        }
      });
    }

    if (mode === "edit") {
      const getFormatDefaultValue = () => {
        // Форматируем значение под тип данных
        const getFormatForDataTypeId = (defaultValue) => {
          switch (selectedCharType?.dataTypeId) {
            case 3:
              return defaultValue ? toHttpFormatStartOfDay(defaultValue) : undefined;
            default:
              return defaultValue;
          }
        };

        // Если есть значения, значит в defaultValue, должно быть оно или ничего
        if (selectedCharType?.charTypeValues && selectedCharType?.charTypeValues?.length > 0) {
          if (fields.defaultValue) {
            const findedCharTypeValue = selectedCharType.charTypeValues?.find(
              (item) => item.id === fields.defaultValue
            );

            if (findedCharTypeValue) {
              return getFormatForDataTypeId(findedCharTypeValue.value);
            }
          }
        }

        return getFormatForDataTypeId(fields.defaultValue);
      };

      const params = {
        query: {
          objectTypeId: Number(objectTypeId),
          objectCharTypeId: Number(objectCharTypeId),
          force: true,
        },
        body: {
          name: fields.name,
          alias: fields.alias,
          accessModeId: Number(fields.accessModeId),
          viewModeMask: Number(fields.viewModeMask),
          minCardinality: fields?.minCardinality !== undefined ? fields?.minCardinality : undefined,
          maxCardinality: fields?.maxCardinality !== undefined ? fields?.maxCardinality : undefined,
          defaultValue: getFormatDefaultValue(),

          charTypeId: typesCharacteristicsObjectById?.charTypeId,
          storageTypeId: typesCharacteristicsObjectById?.storageTypeId,
        },
      };

      dispatch(updateTypesCharacteristicsObject(params)).then((res) => {
        if (res.payload !== undefined) {
          fetchTypesCharacteristicsObjectById();
          setMode("view");
        }
      });
    }
  };

  return (
    <TypesCharacteristicsViewContext.Provider value={contextValue}>
      <div className="types-characteristics-view">
        <Row className="types-characteristics-view-title" align="middle">
          <Link to="/types-characteristics-object">
            <LeftOutlined style={{ fontSize: 16 }} />
          </Link>

          <h1>Просмотр типа характеристики (объект)</h1>

          <Divider className="types-characteristics-view-divider" />
        </Row>

        {checkIsLoading(
          typesCharacteristicsObjectByIdLoading,
          typesCharacteristicsLoading,
          typesCharacteristicsViewModeMaskDicLoading,
          typesCharacteristicsTypesStorageDicLoading,
          typesCharacteristicsByIdLoading
        ) ? (
          <Spin spinning style={{ width: "100%" }} />
        ) : (
          <Form
            form={typesCharacteristicsObjectParamsForm}
            onFinish={onFinish}
            labelAlign="left"
            initialValues={{
              contextId: 1,
              statusId: 0,
            }}
            style={{ width: "100%" }}
          >
            <TypesCharacteristicsObjectParams
              mode={mode}
              typeCharacteristicObject={typesCharacteristicsObjectById}
              typesCharacteristicsObjectParamsForm={typesCharacteristicsObjectParamsForm}
              selectedCharType={selectedCharType}
              onSelectedCharType={onSelectedCharType}
              setMode={setMode}
              targetKeys={targetKeys}
            />

            <Divider
              className="types-characteristics-view-divider"
              style={{ marginTop: "-10px", marginBottom: 0 }}
            />

            {selectedCharType?.charTypeValues && selectedCharType?.charTypeValues?.length > 0 && (
              <TypesCharacteristicsObjectValues
                mode="view"
                typeCharacteristicObject={typesCharacteristicsObjectById}
                typesCharacteristicsObjectParamsForm={typesCharacteristicsObjectParamsForm}
                selectedCharType={selectedCharType}
                targetKeys={targetKeys}
                setTargetKeys={setTargetKeys}
                setDefaultTargetKeys={setDefaultTargetKeys}
                fetchTypesCharacteristicsObjectById={fetchTypesCharacteristicsObjectById}
                setMode={setMode}
              />
            )}
          </Form>
        )}
      </div>
    </TypesCharacteristicsViewContext.Provider>
  );
};

export default TypesCharacteristicsObjectView;
